/**
 * E2. Рулетка: стол с тремя видами ставки (цвет 18/37 ×2, дюжина 12/37 ×3,
 * число 1/37 ×36), выбор сектора, ползунок ставки до половины сбережений
 * (`state.maxBet`) и не больше денег, «Крутить». Шарик бежит 2 с,
 * пропускается; при «меньше движения» — сразу. Один круг за визит: стор
 * принимает один исход в год, поэтому вечер — это один круг (счётчик
 * `rounds` и «казино закрывается» остаются в логике).
 */
import { t } from '@life/i18n';
import { ROULETTE_MAX_ROUNDS, ROULETTE_PAYOUT, ROULETTE_POCKETS, type RouletteInput, type RouletteKind, type RouletteState } from '@life/minigames';
import { useState } from 'preact/hooks';
import { money } from '../lib/format';
import { useReveal } from './reveal';
import type { MinigameScreenProps } from './types';
import { Bet, betCap, useBet } from './ui/Bet';

export const ROULETTE_SPIN_MS = 2000;

/** Выигрышных секторов по виду ставки (из 37). */
const WINNING: Record<RouletteKind, number> = { color: 18, dozen: 12, number: 1 };
const KINDS: readonly RouletteKind[] = ['color', 'dozen', 'number'];

export function Roulette({ state, life, onInput }: MinigameScreenProps<RouletteInput, RouletteState>) {
  const cap = betCap(state.maxBet, life.money);
  const [bet, setBet] = useBet(cap);
  const [kind, setKind] = useState<RouletteKind>('color');
  const [pick, setPick] = useState(0);
  const reveal = useReveal(ROULETTE_SPIN_MS);
  const closed = state.rounds >= ROULETTE_MAX_ROUNDS;
  const canSpin = bet > 0 && !closed && !reveal.running;

  const choose = (k: RouletteKind) => {
    setKind(k);
    setPick(0);
  };

  const spin = () => {
    if (!canSpin) return;
    reveal.begin(() => onInput({ bet, kind, pick }));
  };

  return (
    <>
      <div class="picks" role="group" aria-label={t('ui.minigame.roulette.kinds')}>
        {KINDS.map((k) => (
          <button key={k} type="button" class={k === kind ? 'btn active' : 'btn'} aria-pressed={k === kind} onClick={() => choose(k)}>
            {t(`ui.minigame.roulette.kind.${k}`, { p: WINNING[k], n: ROULETTE_POCKETS, x: ROULETTE_PAYOUT[k] })}
          </button>
        ))}
      </div>
      {kind === 'color' && (
        <div class="picks" role="group" aria-label={t('ui.minigame.roulette.kind.color.short')}>
          {[0, 1].map((p) => (
            <button key={p} type="button" class={p === pick ? 'btn active' : 'btn'} aria-pressed={p === pick} onClick={() => setPick(p)}>
              {t(p === 0 ? 'ui.minigame.roulette.red' : 'ui.minigame.roulette.black')}
            </button>
          ))}
        </div>
      )}
      {kind === 'dozen' && (
        <div class="picks" role="group" aria-label={t('ui.minigame.roulette.kind.dozen.short')}>
          {[0, 1, 2].map((p) => (
            <button key={p} type="button" class={p === pick ? 'btn active' : 'btn'} aria-pressed={p === pick} onClick={() => setPick(p)}>
              {t(`ui.minigame.roulette.dozen.${p}`)}
            </button>
          ))}
        </div>
      )}
      {kind === 'number' && (
        <div class="field">
          <label for="roulette-number">{t('ui.minigame.roulette.number')}</label>
          <input
            id="roulette-number"
            type="number"
            min={0}
            max={ROULETTE_POCKETS - 1}
            step={1}
            value={pick}
            onInput={(e) => {
              const v = Number((e.currentTarget as HTMLInputElement).value);
              setPick(Number.isFinite(v) ? Math.max(0, Math.min(ROULETTE_POCKETS - 1, Math.trunc(v))) : 0);
            }}
          />
        </div>
      )}
      <Bet cap={cap} value={bet} onChange={setBet} />
      {state.rounds === 0 && state.entryFee > 0 && <p class="hint">{t('ui.minigame.roulette.entry', { sum: money(state.entryFee) })}</p>}
      {reveal.running ? (
        <>
          <p class="hint mono">{t('ui.minigame.roulette.spinning', { n: ROULETTE_POCKETS })}</p>
          <button type="button" class="btn ghost" onClick={reveal.skip}>
            {t('ui.minigame.reveal.skip')}
          </button>
        </>
      ) : (
        <button type="button" class="btn accent block" disabled={!canSpin} onClick={spin}>
          {closed ? t('ui.minigame.roulette.closed') : cap > 0 ? t('ui.minigame.roulette.spin') : `${t('ui.minigame.roulette.spin')} — ${t('ui.minigame.bet.none')}`}
        </button>
      )}
    </>
  );
}
